import { type HTMLAttributes, type FunctionComponent, useContext } from 'react';
import { SwatchContext } from '../../../contexts/product-card/swatch.context';
import { type TrackedEvent } from '../../../helpers/analytics/event-types';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { type ProductSwatch } from '../../../types/product.types';

export const DEFAULT_SWATCH_SIZE = 27;

export type ProductCardSwatchProps = {
	swatch: ProductSwatch;
	swatchSize?: number;
	selectEvent?: TrackedEvent;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductCardSwatch: FunctionComponent<ProductCardSwatchProps> = ({
	swatch,
	swatchSize = DEFAULT_SWATCH_SIZE,
	selectEvent,
	className = ''
}) => {
	const trackEvent = useTrackEvent();

	const { hoveredSwatch, selectedSwatch, setHoveredSwatch, setSelectedSwatch } = useContext(SwatchContext);

	const isActive = (hoveredSwatch ? hoveredSwatch?.variantId : selectedSwatch.variantId) === swatch.variantId;

	const handleSelect = () => {
		setSelectedSwatch(swatch);
		if (selectEvent) {
			trackEvent({ event: selectEvent });
		}
	};

	return (
		<div
			aria-checked={isActive}
			aria-label={`${swatch.name} finish`}
			tabIndex={0}
			className="flex bw2 br b--theme-white"
			onClick={handleSelect}
			onKeyDown={handleKeys(['Enter', ' '], handleSelect)}
			onMouseEnter={() => setHoveredSwatch(swatch)}
			onMouseLeave={() => setHoveredSwatch(undefined)}
			data-testid={`swatchContent-${swatch.variantId}`}
			data-automation="finish-swatch"
			role="checkbox">
			<div className={`ba br1 pointer outline-0 bw1 ${isActive ? 'b--theme-primary' : 'b--theme-grey-lighter'} ${className}`}>
				<div
					title={swatch.name}
					data-testid={`${swatch.name.toLowerCase()}-swatch-${swatchSize}`}
					style={{
						backgroundColor: `#${swatch.color}`,
						width: `${swatchSize}px`,
						height: `${swatchSize}px`,
						margin: '2px'
					}}
				/>
			</div>
		</div>
	);
};
